import {
  EVariableType,
  PdmProperties,
  EPDMProperty,
  ConfigitProperties,
  EPropertyType,
  EMessagesType,
} from '../data/Constants';
import {
  addSectionsToFlatList,
  getFeatureCode,
  getModelSections,
  getSubModelParents,
  getValueById,
  getVariableType,
} from './ConfigurationDataHelperFunctions';
import { type GetThunkAPI, type AsyncThunkConfig } from '@reduxjs/toolkit';
import { getErrorData } from '../store/Helpers/ConfigurationSliceHelper';
import { setError } from '../store/states/ErrorSlice';
import { t } from 'i18next';
import { setMessage } from '../store/states/MessagesSlice';

import type {
  IConfiguration,
  IConfigurationVariable,
  ISection,
  IValueType,
  IConfigurationValue,
  IConfigurationProperty,
  IPropertyAssociation,
  IPropertyDefinition,
  IMaterialPropertyAssociations,
  IPropertyBase,
  ILoadConfiguration,
  IPdmProperty,
} from '../../types';
import type { IError } from '../../types/IApiTypes';
/**
 * This method returns the configuration data with pdm properties
 * @param {IConfiguration} data the configuration response
 * @param {Map<string,Map<string, IMaterialPropertyAssociations>>} propertyAssociations list of  property associations
 * @param {Array<PropertyDefinition> }propertyDefinition list of property definitions
 * @returns {IConfiguration} return the configuration data with updated pdm properties
 */

export function updateProperties(
  data: IConfiguration | null | undefined,
  propertyAssociations: Map<string, Map<string, IMaterialPropertyAssociations>>,
  propertyDefinition: Array<IPropertyDefinition>
): IConfiguration | null | undefined {
  if (data) {
    data.sections = addPdmProperties(data.sections, propertyAssociations, propertyDefinition);
  }
  return data;
}

/**
 * Update the variable and value properties to pdm propertiy values
 * @param {ISection[]} sections list of sections
 * @param {Map<string,Map<string, IMaterialPropertyAssociations>>} propertyAssociations list of property association response
 * @param {Array<PropertyDefinition>}propertyDefinition list of property definitions
 * @returns {ISection[]} returns the list of section and  updated variable pdm properties
 */

export function addPdmProperties(
  sections: ISection[],
  propertyAssociations: Map<string, Map<string, IMaterialPropertyAssociations>>,
  propertyDefinition: Array<IPropertyDefinition>
): ISection[] {
  if (propertyAssociations.size > 0) {
    sections.forEach((section) => {
      section.variables = updateVariableProperties(
        section.variables,
        propertyAssociations,
        propertyDefinition
      );
      section.sections = addPdmProperties(
        section.sections,
        propertyAssociations,
        propertyDefinition
      );
    });
  }
  return sections;
}

/**
 * Update the  variable properties of the section
 * @param {IConfigurationVariable} variables  variable array
 * @param {Map<string,Map<string, IMaterialPropertyAssociations>>} propertyAssociations  property association list
 * @param {IPropertyDefinition} propertyDefinition  property definition list
 * @returns {IConfigurationVariable} returns the list of variable with updated
 */

function updateVariableProperties(
  variables: IConfigurationVariable[],
  propertyAssociations: Map<string, Map<string, IMaterialPropertyAssociations>>,
  propertyDefinition: Array<IPropertyDefinition>
): IConfigurationVariable[] {
  let association;
  variables?.map((variable: IConfigurationVariable) => {
    const id = getFeatureCode(variable.id, variable.variableType);
    association = getValueById(propertyAssociations, id);
    mapPdmProperties(variable, association?.associations ?? [], propertyDefinition);
    if (variable?.values) {
      updateValueProperties(variable?.values, propertyAssociations, propertyDefinition);
    }
  });
  return variables;
}

/**
 * Update the option properties of the feature families
 * @param {IConfigurationValue} values  values array
 * @param {Map<string,Map<string, IMaterialPropertyAssociations>>} propertyAssociations  property association list
 * @param {IPropertyDefinition} propertyDefinition  property definition list
 * @returns {IConfigurationValue} returns the list of variable
 */

function updateValueProperties(
  values: IConfigurationValue[],
  propertyAssociations: Map<string, Map<string, IMaterialPropertyAssociations>>,
  propertyDefinition: Array<IPropertyDefinition>
): void {
  let association;
  values?.forEach((value: IConfigurationValue) => {
    const id = value?.value?.toString();
    association = getValueById(propertyAssociations, id);
    mapPdmProperties(value, association?.associations ?? [], propertyDefinition);
  });
}
/**
 * Add Pdm properties to the  variable and values
 * @param {IConfigurationVariable } variable the configuration variable
 * @param {IPropertyAssociation[]} pdmProperties list of property associations
 * @param {Array<IpropertyDefinition> }propertyDefinition list property definitions
 * @returns {void} added pdm properties to  the variables and values
 */

export function mapPdmProperties(
  variable: IConfigurationVariable | IConfigurationValue,
  pdmProperties: IPropertyAssociation[],
  propertyDefinition: Array<IPropertyDefinition>
) {
  if (variable.properties) {
    variable.pdmProperties = {} as IPdmProperty;
    PdmProperties.forEach((property: IPropertyBase) => {
      const pdmProperty =
        pdmProperties.length > 0 && getAssociationPropertiesById(pdmProperties, property.code);
      const defautProperty: IPropertyDefinition | undefined = getDefaultProperty(
        propertyDefinition,
        property.code
      );
      const defaultValue = getDefaultValue(defautProperty);
      Object.assign(variable.pdmProperties, { [property.name]: defaultValue });
      if (pdmProperty) {
        const value = getValue(pdmProperty, defautProperty?.type ?? '');
        Object.assign(variable.pdmProperties, { [property.name]: value });
      }
    });
  }
}
/**
 * This function returns the property value from the property association
 * @param {IPropertyAssociation} pdmProperty  pdm propeerty list
 * @param {string} type  property type
 * @returns {IValueType} returns the property value
 */

const getValue = (pdmProperty: IPropertyAssociation, type: string): IValueType => {
  let value = pdmProperty.name;
  if (type === EPropertyType.Bool) {
    value = String(pdmProperty?.name).toLowerCase() === 'true';
  }

  return value;
};

/**
 * This function returns the default value from the property definition
 * @param { IPropertyAssociation } propertyDefinition property definition
 * @returns { string| undefined } returns the default property value
 */

const getDefaultValue = (
  propertyDefinition: IPropertyDefinition | undefined
): string | boolean | undefined => {
  let defaultValue: string | boolean | undefined = propertyDefinition?.default?.code;
  if (propertyDefinition?.type === EPropertyType.Bool) {
    defaultValue = String(defaultValue).toLowerCase() === 'true';
  }

  return defaultValue;
};
/**
 * This method extracts the product level ORI Properties
 * @param {Map<string, Map<string, IMaterialPropertyAssociations>>} propertyAssociations  property associations map
 * @param {String} modelId  product id
 * @returns {string | null} ordering instruction property name or null
 */

export function getProductORIProperty(
  propertyAssociations: Map<string, Map<string, IMaterialPropertyAssociations>>,
  modelId: string
): string | null {
  if (propertyAssociations.size > 0) {
    const product = getValueById(propertyAssociations, modelId);
    if (product) {
      const hasORIProperty = getAssociationPropertiesById(
        product.associations,
        EPDMProperty.OrderingInstructions
      );
      if (hasORIProperty) {
        return hasORIProperty.name as string | null;
      }
    }
  }
  return null;
}

//This method returns the property from the configit properties
export function getAssociationPropertiesById(
  properties: IPropertyAssociation[],
  id: string
): IPropertyAssociation | undefined {
  return properties.find((property) => property.code === id);
}

/**
 * This function shows the error dialog if property definition state is empty
 * Reset the error window once get the property definition response from the Api
 * @param {PropertyDefinition[]} properytyDefinition  properytyDefinition list
 * @param {{ dispatch: AppDispatch }} thunkAPI  thunk Api object
 * @returns {void}
 */

export function RaisePropertyDefinitionMissingError(
  properytyDefinition: IPropertyDefinition[],
  thunkAPI: GetThunkAPI<AsyncThunkConfig>
): void {
  const hasMandatoryProperties = checkIfPdmMandatoryPropertiesExists(properytyDefinition);
  if (!hasMandatoryProperties) {
    const errorObj: IError = getErrorData(t);

    const errPayload = {
      code: errorObj?.response?.status ?? errorObj.code,
      message: errorObj?.response?.data ? errorObj.response.data.message : errorObj.message,
      page: errorObj.page,
      show: true,
    };

    thunkAPI.dispatch(setError(errPayload));
  }
}

/**
 * This function shows the warning message in the Notification drawer if any mandatory configit properties are missing in the response
 * Check MultiSelect or Single select feature family  properties
 * @param {ILoadConfiguration}  configuration configuration read response
 * @param {GetThunkAPI<AsyncThunkOptions>} thunkAPI  thunk Api object
 * @returns {void}
 */

export function RaiseMandatoryMissingConfigitPropertiesError(
  configuration: ILoadConfiguration,
  thunkAPI: GetThunkAPI<AsyncThunkConfig>
): void {
  const sections = configuration?.configureResponse?.sections ?? [];

  //check mandatory properties for root model and its submodels
  const hasMandatoryConfigitProperties =
    checkIfConfigitPropertiesExist(sections) &&
    checkIfSubModelConfigitPropertiesExist(configuration);

  //Show the warning in the notification drawer if mandatory properties are missing
  if (!hasMandatoryConfigitProperties) {
    thunkAPI.dispatch(
      setMessage({
        text: t('errorMessages.configitProperyNotActive'),
        type: EMessagesType.Info,
      })
    );
  }
}

/**
 *This function looping all the submodels and check mandatory properties for its feature and options
 * @param {ILoadConfiguration} configuration the read configure response
 * @returns {boolean} returns true or false
 */

export function checkIfSubModelConfigitPropertiesExist(configuration: ILoadConfiguration): boolean {
  const subModels = configuration?.modelContext?.subModels;
  const configurationResponse = configuration?.configureResponse;
  const rootProductId = configuration?.modelContext?.rootModel?.id;

  // returns true if submodels length is 0
  if (subModels?.length === 0) {
    return true;
  }

  //extract all the sections from the configure response
  const flatSections: ISection[] = [];
  addSectionsToFlatList(configurationResponse ?? undefined, flatSections);

  let hasMandatoryConfigitProperties = false;

  //loop all the submodels and check the mandatory properties for its feature and options
  subModels.forEach((currentSubModel) => {
    const subModelId = currentSubModel?.subModelVariableIds?.[0] ?? '';
    const parents = getSubModelParents(subModelId, rootProductId);
    const sections = getModelSections(flatSections, parents, currentSubModel.id);

    hasMandatoryConfigitProperties = checkIfConfigitPropertiesExist(sections);
  });

  return hasMandatoryConfigitProperties;
}

/**
 * This function checks the mandatory propeties in the  first  feature family in section
 * @param {ISection[]} sections the section list
 * @returns {boolean} returns the true or false
 */

export function checkIfConfigitPropertiesExist(sections: ISection[]): boolean {
  let hasMandatoryConfigitProperties = false;
  for (const section of sections) {
    //get the single or multi feature families from the sections.
    let variable = section.variables.find((variable: IConfigurationVariable) =>
      [EVariableType.Singleselect, EVariableType.Multiselect].includes(
        getVariableType(variable) as EVariableType
      )
    );

    variable ??= section.variables.find((variable: IConfigurationVariable) =>
      [EVariableType.Number, EVariableType.String].includes(
        getVariableType(variable) as EVariableType
      )
    );

    //check if all mandatory configit properties are available in any multi or single select feature families.
    if (variable) {
      const variableType = getVariableType(variable);
      if (
        variableType === EVariableType.Singleselect ||
        variableType === EVariableType.Multiselect
      ) {
        hasMandatoryConfigitProperties =
          checkIfConfigitMandatoryPropertiesExists(variable.properties) &&
          checkIfConfigitMandatoryPropertiesExists(variable.values[0].properties);
      } else if (variableType === EVariableType.String || variableType === EVariableType.Number) {
        hasMandatoryConfigitProperties = checkIfConfigitMandatoryPropertiesExists(
          variable.properties
        );
      }
      //stop the loop if  validate the configit propeties for atleast one variable and values
      break;
    }
  }
  return hasMandatoryConfigitProperties;
}
/**
 * Check the Mandatory properties exist into the configure response section properties values
 * @param {Array<IConfigurationProperty>} configitProperties receive from the response
 * @returns {boolean} returns the true or false
 */

export const checkIfConfigitMandatoryPropertiesExists = (
  configitProperties: Array<IConfigurationProperty> | null | undefined
): boolean => {
  if (!configitProperties) {
    return false;
  }

  const mandatoryConfigitProperties = (property: string) => {
    return configitProperties?.find(
      (configitProperty: IConfigurationProperty) => configitProperty.id === property
    );
  };

  const configItMandatoryPropertyExit = ConfigitProperties.filter(mandatoryConfigitProperties);
  return configItMandatoryPropertyExit.length === ConfigitProperties.length;
};
/**
 * Check the Mandatory properties exist into the propertyDefinition
 * @param {Array<PropertyDefinition> } propertyDefinition list of property definitions
 * @returns {boolean} returns the true or false
 */
export const checkIfPdmMandatoryPropertiesExists = (
  propertyDefinition: Array<IPropertyDefinition>
): boolean => {
  const mandatoryPdmProperties = (mandatoryPdmProperty: IPropertyBase) => {
    return getDefaultProperty(propertyDefinition, mandatoryPdmProperty.code);
  };

  const pdmMandatoryPropertyExits = PdmProperties.filter(mandatoryPdmProperties);
  return pdmMandatoryPropertyExits.length === PdmProperties.length;
};

/**
 * Find the mandatory property from the propertyDefinition
 * @param {Array<IPropertyDefinition>} propertyDefinition list of property definitions
 * @param {string} property current property code
 * @returns {IPropertyDefinition|undefined} returns the default value of the property
 */
const getDefaultProperty = (
  propertyDefinition: Array<IPropertyDefinition>,
  property: string
): IPropertyDefinition | undefined => {
  return propertyDefinition?.find(
    (propertyDef: IPropertyDefinition) => propertyDef.code === property
  );
};
